// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-18) — when a time series was highest and lowest
 * (docs/AYNI_XR_WHEN_EXTREMES_PLAN.md).
 *
 * "If I am a researcher analysing a time series, can I find WHEN we got the
 * highest or lowest measurement?" Every series the app keeps (a pin's, the
 * area over a threshold, a region through time, the Line over time) answers
 * it the same way, here:
 *
 * - **Only what was read.** A series fills as playback passes the frames;
 *   an unread frame is not a low one. The answer carries how many frames it
 *   rests on, so the panel can say "from 140 of 480 frames read".
 * - **Ties are said, not hidden.** The data is 8-bit: a field that clips at
 *   the top of its scale has the same highest value in many frames. The
 *   first in time is named, and the others are counted ("and 3 other
 *   frames"), so nobody reads one date as *the* record when it is one of
 *   several.
 * - **Exact equality is a tie.** Two frames whose codes decode to the same
 *   value are tied; values a step apart are not. Within one quantisation
 *   step is still a real difference in what the frame holds, and the panel
 *   prints the step beside the value.
 *
 * Pure: no DOM.
 */

import type { MeasurementSeries } from './series'

/** One end of a series: its value, the first frame (by index) that holds it, and every frame that does. */
export interface WhenExtreme {
  readonly value: number
  /** The first index (in time) holding the value. */
  readonly index: number
  /** Every index holding exactly this value, in order (`index` first). */
  readonly ties: readonly number[]
}

export interface WhenExtremes {
  readonly max: WhenExtreme
  readonly min: WhenExtreme
  /** Items with a value. */
  readonly withData: number
}

/**
 * The highest and lowest of `count` items, `valueAt(i)` null (or not finite)
 * where an item has none. Null when no item has a value.
 */
export function extremesOf(count: number, valueAt: (i: number) => number | null | undefined): WhenExtremes | null {
  let max = -Infinity
  let min = Infinity
  let withData = 0
  const n = Math.max(0, Math.floor(count))
  for (let i = 0; i < n; i++) {
    const v = valueAt(i)
    if (v === null || v === undefined || !Number.isFinite(v)) continue
    withData++
    if (v > max) max = v
    if (v < min) min = v
  }
  if (!withData) return null
  const maxTies: number[] = []
  const minTies: number[] = []
  for (let i = 0; i < n; i++) {
    const v = valueAt(i)
    if (v === max) maxTies.push(i)
    if (v === min) minTies.push(i)
  }
  return {
    max: { value: max, index: maxTies[0], ties: maxTies },
    min: { value: min, index: minTies[0], ties: minTies },
    withData,
  }
}

/** One end of a pin's series: its value, the frame's time, and where it was read (a pin that follows the extreme). */
export interface SeriesWhen {
  readonly value: number
  /** The frame's time (UTC ms, or playback ms for a dataset without dates). */
  readonly timeMs: number
  /** The slot it sits in. */
  readonly slot: number
  /** Where it was read, for a pin that follows the highest or lowest value. */
  readonly at?: { lat: number; lon: number }
  /** Other frames holding exactly this value. */
  readonly others: number
}

export interface SeriesWhenExtremes {
  readonly max: SeriesWhen
  readonly min: SeriesWhen
  /** Slots read (with data or not), and the slots in the window. */
  readonly read: number
  readonly total: number
  readonly withData: number
}

/** The time a slot stands for: the frame read into it, else its place in the window. */
export function slotTimeMs(series: MeasurementSeries, slot: number): number {
  const s = series.slots[slot]
  if (s?.timeMs !== undefined && Number.isFinite(s.timeMs)) return s.timeMs
  const last = Math.max(1, series.slots.length - 1)
  return series.startMs + ((series.endMs - series.startMs) * slot) / last
}

/**
 * When a pin's series was highest and lowest, over the frames read. Read on
 * the series itself (a slot per frame), never on the graph's thinned line,
 * whose columns average several frames.
 */
export function seriesWhenExtremes(series: MeasurementSeries | null | undefined): SeriesWhenExtremes | null {
  if (!series) return null
  const slots = series.slots
  const found = extremesOf(slots.length, (i) => (slots[i].sampled ? slots[i].value : null))
  if (!found) return null
  let read = 0
  for (const s of slots) if (s.sampled) read++
  const end = (e: WhenExtreme): SeriesWhen => {
    const at = slots[e.index].at
    return { value: e.value, timeMs: slotTimeMs(series, e.index), slot: e.index, ...(at ? { at: { ...at } } : {}), others: e.ties.length - 1 }
  }
  return { max: end(found.max), min: end(found.min), read, total: slots.length, withData: found.withData }
}
